/** @module @liquidefy/code-editor/document/document-history @version 1.0.0 @license LQD Research LLC */

// ═══ §1 IMPORTS ═══

import type {
  EditorResult,
  EditorEvent,
  EditOperation,
  CursorState,
  HistoryEntry,
  HistoryBranch,
  HistoryState,
} from '../code-editor-types.js';

import {
  ok,
  err,
  now,
  generateId,
  MAX_UNDO_STACK,
} from '../code-editor.js';

// ═══ §2 HISTORY MANAGEMENT — push, undo, redo, multi-step ═══

export function pushToHistory(
  state: HistoryState,
  operations: EditOperation[],
  cursors: { before: CursorState[]; after: CursorState[] },
): EditorResult<HistoryState> {
  if (operations.length === 0) {
    return ok(state, 'NOOP', 'No operations to record');
  }

  const entry: HistoryEntry = {
    id: generateId('he'),
    operations,
    beforeCursors: cursors.before,
    afterCursors: cursors.after,
    timestamp: now(),
  };

  const trimmedEntries = state.currentIndex < state.entries.length - 1
    ? state.entries.slice(0, state.currentIndex + 1)
    : state.entries;

  const newEntries = [...trimmedEntries, entry];
  const capped = newEntries.length > MAX_UNDO_STACK
    ? newEntries.slice(newEntries.length - MAX_UNDO_STACK)
    : newEntries;

  const updated: HistoryState = {
    ...state,
    entries: capped,
    currentIndex: capped.length - 1,
  };

  const event: EditorEvent = {
    kind: 'changed',
    domain: 'history',
    timestamp: now(),
    payload: { action: 'push', entryId: entry.id, operationCount: operations.length },
  };

  return ok(updated, 'HISTORY_PUSHED', 'Entry pushed to history', [event]);
}

export function undoOperation(
  state: HistoryState,
): EditorResult<{ state: HistoryState; entry: HistoryEntry }> {
  if (!canUndo(state)) {
    return err('NOTHING_TO_UNDO', 'No operations to undo');
  }

  const entry = state.entries[state.currentIndex];
  const updated: HistoryState = {
    ...state,
    currentIndex: state.currentIndex - 1,
  };

  const event: EditorEvent = {
    kind: 'changed',
    domain: 'history',
    timestamp: now(),
    payload: { action: 'undo', entryId: entry!.id },
  };

  return ok({ state: updated, entry: entry! }, 'UNDO_PERFORMED', 'Undo performed', [event]);
}

export function redoOperation(
  state: HistoryState,
): EditorResult<{ state: HistoryState; entry: HistoryEntry }> {
  if (!canRedo(state)) {
    return err('NOTHING_TO_REDO', 'No operations to redo');
  }

  const entry = state.entries[state.currentIndex + 1];
  const updated: HistoryState = {
    ...state,
    currentIndex: state.currentIndex + 1,
  };

  const event: EditorEvent = {
    kind: 'changed',
    domain: 'history',
    timestamp: now(),
    payload: { action: 'redo', entryId: entry!.id },
  };

  return ok({ state: updated, entry: entry! }, 'REDO_PERFORMED', 'Redo performed', [event]);
}

export function undoToEntry(
  state: HistoryState,
  entryId: string,
): EditorResult<{ state: HistoryState; entries: HistoryEntry[] }> {
  const targetIndex = state.entries.findIndex(e => e.id === entryId);
  if (targetIndex < 0) {
    return err('ENTRY_NOT_FOUND', `History entry ${entryId} not found`);
  }
  if (targetIndex >= state.currentIndex) {
    return err('INVALID_UNDO', 'Target entry is at or after current position');
  }

  const undone = state.entries.slice(targetIndex + 1, state.currentIndex + 1).reverse();
  const updated: HistoryState = {
    ...state,
    currentIndex: targetIndex,
  };

  const event: EditorEvent = {
    kind: 'changed',
    domain: 'history',
    timestamp: now(),
    payload: { action: 'undoTo', entryId, count: undone.length },
  };

  return ok({ state: updated, entries: undone }, 'UNDO_TO_PERFORMED', `Undid ${undone.length} operations`, [event]);
}

export function redoToEntry(
  state: HistoryState,
  entryId: string,
): EditorResult<{ state: HistoryState; entries: HistoryEntry[] }> {
  const targetIndex = state.entries.findIndex(e => e.id === entryId);
  if (targetIndex < 0) {
    return err('ENTRY_NOT_FOUND', `History entry ${entryId} not found`);
  }
  if (targetIndex <= state.currentIndex) {
    return err('INVALID_REDO', 'Target entry is at or before current position');
  }

  const redone = state.entries.slice(state.currentIndex + 1, targetIndex + 1);
  const updated: HistoryState = {
    ...state,
    currentIndex: targetIndex,
  };

  const event: EditorEvent = {
    kind: 'changed',
    domain: 'history',
    timestamp: now(),
    payload: { action: 'redoTo', entryId, count: redone.length },
  };

  return ok({ state: updated, entries: redone }, 'REDO_TO_PERFORMED', `Redid ${redone.length} operations`, [event]);
}

// ═══ §3 BRANCHING — create, switch, merge, list, prune ═══

export function createHistoryBranch(
  state: HistoryState,
  label?: string,
): EditorResult<HistoryState> {
  if (state.currentIndex < 0) {
    return err('NO_HISTORY', 'No history entries to branch from');
  }

  const branchId = label ?? generateId('hb');
  const parentEntryId = state.entries[state.currentIndex]!.id;

  const futureEntries = state.currentIndex < state.entries.length - 1
    ? state.entries.slice(state.currentIndex + 1)
    : [];

  const newBranch: HistoryBranch = {
    id: branchId,
    parentEntryId,
    entries: futureEntries,
  };

  const mainEntries = state.entries.slice(0, state.currentIndex + 1);

  const updated: HistoryState = {
    ...state,
    entries: mainEntries,
    branches: [...state.branches, newBranch],
  };

  const event: EditorEvent = {
    kind: 'created',
    domain: 'history',
    timestamp: now(),
    payload: { action: 'branchCreated', branchId, parentEntryId },
  };

  return ok(updated, 'BRANCH_CREATED', `Branch "${branchId}" created`, [event]);
}

export function switchHistoryBranch(
  state: HistoryState,
  branchId: string,
): EditorResult<HistoryState> {
  const branchIndex = state.branches.findIndex(b => b.id === branchId);
  if (branchIndex < 0) {
    return err('BRANCH_NOT_FOUND', `Branch "${branchId}" not found`);
  }

  const branch = state.branches[branchIndex];
  const parentIdx = state.entries.findIndex(e => e.id === branch!.parentEntryId);
  if (parentIdx < 0) {
    return err('PARENT_NOT_FOUND', 'Branch parent entry not found in main history');
  }

  const currentFuture = state.entries.slice(parentIdx + 1);
  const currentBranch: HistoryBranch = {
    id: state.activeBranch,
    parentEntryId: branch!.parentEntryId,
    entries: currentFuture,
  };

  const newBranches = state.branches.filter((_, i) => i !== branchIndex);
  if (currentFuture.length > 0) {
    newBranches.push(currentBranch);
  }

  const mainEntries = [...state.entries.slice(0, parentIdx + 1), ...branch!.entries];

  const updated: HistoryState = {
    entries: mainEntries,
    currentIndex: mainEntries.length - 1,
    branches: newBranches,
    activeBranch: branchId,
  };

  const event: EditorEvent = {
    kind: 'changed',
    domain: 'history',
    timestamp: now(),
    payload: { action: 'branchSwitched', from: state.activeBranch, to: branchId },
  };

  return ok(updated, 'BRANCH_SWITCHED', `Switched to branch "${branchId}"`, [event]);
}

export function mergeHistoryBranches(
  state: HistoryState,
  sourceBranch: string,
  targetBranch: string,
): EditorResult<HistoryState> {
  const sourceIdx = state.branches.findIndex(b => b.id === sourceBranch);
  if (sourceIdx < 0) {
    return err('BRANCH_NOT_FOUND', `Source branch "${sourceBranch}" not found`);
  }

  const source = state.branches[sourceIdx];

  if (targetBranch === state.activeBranch) {
    const updated: HistoryState = {
      ...state,
      entries: [...state.entries, ...source!.entries],
      currentIndex: state.entries.length + source!.entries.length - 1,
      branches: state.branches.filter((_, i) => i !== sourceIdx),
    };

    const event: EditorEvent = {
      kind: 'changed',
      domain: 'history',
      timestamp: now(),
      payload: { action: 'branchMerged', source: sourceBranch, target: targetBranch },
    };

    return ok(updated, 'BRANCH_MERGED', `Merged "${sourceBranch}" into "${targetBranch}"`, [event]);
  }

  const targetIdx = state.branches.findIndex(b => b.id === targetBranch);
  if (targetIdx < 0) {
    return err('BRANCH_NOT_FOUND', `Target branch "${targetBranch}" not found`);
  }

  const target = state.branches[targetIdx]!;
  const mergedBranch: HistoryBranch = {
    ...target,
    entries: [...target.entries, ...source!.entries],
  };

  const newBranches = state.branches.filter((_, i) => i !== sourceIdx && i !== targetIdx);
  newBranches.push(mergedBranch);

  const updated: HistoryState = {
    ...state,
    branches: newBranches,
  };

  const event: EditorEvent = {
    kind: 'changed',
    domain: 'history',
    timestamp: now(),
    payload: { action: 'branchMerged', source: sourceBranch, target: targetBranch },
  };

  return ok(updated, 'BRANCH_MERGED', `Merged "${sourceBranch}" into "${targetBranch}"`, [event]);
}

export function listBranches(state: HistoryState): Array<{ id: string; entryCount: number; parentEntryId: string }> {
  return state.branches.map(b => ({
    id: b.id,
    entryCount: b.entries.length,
    parentEntryId: b.parentEntryId,
  }));
}

export function branchDepth(state: HistoryState, branchId: string): number {
  const branch = state.branches.find(b => b.id === branchId);
  if (!branch) return 0;
  return branch.entries.length;
}

export function pruneEmptyBranches(state: HistoryState): EditorResult<HistoryState> {
  const nonEmpty = state.branches.filter(b => b.entries.length > 0);
  const pruned = state.branches.length - nonEmpty.length;

  if (pruned === 0) return ok(state, 'NOOP', 'No empty branches to prune');

  const updated: HistoryState = { ...state, branches: nonEmpty };

  const event: EditorEvent = {
    kind: 'deleted',
    domain: 'history',
    timestamp: now(),
    payload: { action: 'branchesPruned', count: pruned },
  };

  return ok(updated, 'BRANCHES_PRUNED', `Pruned ${pruned} empty branches`, [event]);
}

// ═══ §4 GROUPING — undo groups and time-based coalescing ═══

export function beginUndoGroup(state: HistoryState, groupId: string): EditorResult<HistoryState> {
  const lastEntry = state.currentIndex >= 0 ? state.entries[state.currentIndex] : null;
  if (lastEntry && lastEntry.groupId !== undefined) {
    return err('GROUP_ACTIVE', 'An undo group is already active');
  }

  return ok(
    { ...state, activeBranch: state.activeBranch },
    'GROUP_STARTED',
    `Undo group "${groupId}" started`,
    [{
      kind: 'started',
      domain: 'history',
      timestamp: now(),
      payload: { action: 'groupStarted', groupId },
    }],
  );
}

export function endUndoGroup(state: HistoryState, groupId: string): EditorResult<HistoryState> {
  return ok(
    state,
    'GROUP_ENDED',
    `Undo group "${groupId}" ended`,
    [{
      kind: 'completed',
      domain: 'history',
      timestamp: now(),
      payload: { action: 'groupEnded', groupId },
    }],
  );
}

export function isInUndoGroup(state: HistoryState): boolean {
  if (state.currentIndex < 0) return false;
  return state.entries[state.currentIndex]!.groupId !== undefined;
}

export function coalesceEntries(
  entries: HistoryEntry[],
  maxTimeGap: number,
): HistoryEntry[] {
  if (entries.length <= 1) return entries;

  const coalesced: HistoryEntry[] = [entries[0]!];

  for (let i = 1; i < entries.length; i++) {
    const prev = coalesced[coalesced.length - 1];
    const curr = entries[i];
    const gap = curr!.timestamp - prev!.timestamp;

    if (gap <= maxTimeGap && prev!.groupId === curr!.groupId) {
      coalesced[coalesced.length - 1] = {
        ...prev!,
        operations: [...prev!.operations, ...curr!.operations],
        afterCursors: curr!.afterCursors,
        timestamp: curr!.timestamp,
      };
    } else {
      coalesced.push(curr!);
    }
  }

  return coalesced;
}

// ═══ §5 ANALYSIS — stack depths, sizes, oldest/newest ═══

export function canUndo(state: HistoryState): boolean {
  return state.currentIndex >= 0;
}

export function canRedo(state: HistoryState): boolean {
  return state.currentIndex < state.entries.length - 1;
}

export function undoStackDepth(state: HistoryState): number {
  return state.currentIndex + 1;
}

export function redoStackDepth(state: HistoryState): number {
  return state.entries.length - state.currentIndex - 1;
}

export function historySize(state: HistoryState): number {
  let total = state.entries.length;
  for (const branch of state.branches) {
    total += branch.entries.length;
  }
  return total;
}

export function historyMemoryEstimate(state: HistoryState): number {
  let bytes = 0;

  const estimateEntry = (entry: HistoryEntry): number => {
    let size = 64;
    for (const op of entry.operations) {
      size += 48 + op.text.length * 2;
    }
    size += entry.beforeCursors.length * 24;
    size += entry.afterCursors.length * 24;
    return size;
  };

  for (const entry of state.entries) {
    bytes += estimateEntry(entry);
  }
  for (const branch of state.branches) {
    bytes += 32;
    for (const entry of branch.entries) {
      bytes += estimateEntry(entry);
    }
  }

  return bytes;
}

export function oldestEntry(state: HistoryState): HistoryEntry | null {
  if (state.entries.length === 0) return null;
  return state.entries[0]!;
}

export function newestEntry(state: HistoryState): HistoryEntry | null {
  if (state.entries.length === 0) return null;
  return state.entries[state.entries.length - 1]!;
}

// ═══ §6 CLEANUP — trim, clear, compact, serialize, deserialize ═══

export function trimHistory(state: HistoryState, maxEntries: number): EditorResult<HistoryState> {
  if (state.entries.length <= maxEntries) {
    return ok(state, 'NOOP', 'History within limit');
  }

  const trimCount = state.entries.length - maxEntries;
  const trimmed = state.entries.slice(trimCount);
  const newIndex = Math.max(-1, state.currentIndex - trimCount);

  const updatedBranches = state.branches.filter(b => {
    return trimmed.some(e => e.id === b.parentEntryId);
  });

  const updated: HistoryState = {
    ...state,
    entries: trimmed,
    currentIndex: newIndex,
    branches: updatedBranches,
  };

  const event: EditorEvent = {
    kind: 'changed',
    domain: 'history',
    timestamp: now(),
    payload: { action: 'trimmed', removedCount: trimCount },
  };

  return ok(updated, 'HISTORY_TRIMMED', `Trimmed ${trimCount} entries`, [event]);
}

export function clearHistory(state: HistoryState): EditorResult<HistoryState> {
  const updated: HistoryState = {
    entries: [],
    currentIndex: -1,
    branches: [],
    activeBranch: state.activeBranch,
  };

  const event: EditorEvent = {
    kind: 'deleted',
    domain: 'history',
    timestamp: now(),
    payload: { action: 'cleared' },
  };

  return ok(updated, 'HISTORY_CLEARED', 'History cleared', [event]);
}

export function compactHistory(state: HistoryState): EditorResult<HistoryState> {
  if (state.entries.length <= 1) {
    return ok(state, 'NOOP', 'Nothing to compact');
  }

  const compacted: HistoryEntry[] = [state.entries[0]!];

  for (let i = 1; i < state.entries.length; i++) {
    const prev = compacted[compacted.length - 1];
    const curr = state.entries[i];

    const isTiny = curr!.operations.length === 1 &&
      curr!.operations[0]!.kind === 'insert' &&
      curr!.operations[0]!.text.length <= 2;

    const prevIsTiny = prev!.operations.length === 1 &&
      prev!.operations[0]!.kind === 'insert' &&
      prev!.operations[0]!.text.length <= 2;

    const timeClose = curr!.timestamp - prev!.timestamp < 500;

    if (isTiny && prevIsTiny && timeClose && prev!.groupId === curr!.groupId) {
      compacted[compacted.length - 1] = {
        ...prev!,
        operations: [...prev!.operations, ...curr!.operations],
        afterCursors: curr!.afterCursors,
        timestamp: curr!.timestamp,
      };
    } else {
      compacted.push(curr!);
    }
  }

  const merged = state.entries.length - compacted.length;
  if (merged === 0) return ok(state, 'NOOP', 'Nothing to compact');

  const newIndex = Math.min(state.currentIndex, compacted.length - 1);

  const updated: HistoryState = {
    ...state,
    entries: compacted,
    currentIndex: newIndex,
  };

  const event: EditorEvent = {
    kind: 'changed',
    domain: 'history',
    timestamp: now(),
    payload: { action: 'compacted', mergedCount: merged, newSize: compacted.length },
  };

  return ok(updated, 'HISTORY_COMPACTED', `Compacted ${merged} entries`, [event]);
}

export function serializeHistory(state: HistoryState): {
  entries: Array<{
    id: string;
    operations: Array<{ kind: string; range: { start: { line: number; column: number }; end: { line: number; column: number } }; text: string; origin: string }>;
    beforeCursors: Array<{ position: { line: number; column: number }; desiredColumn: number }>;
    afterCursors: Array<{ position: { line: number; column: number }; desiredColumn: number }>;
    timestamp: number;
    groupId?: string;
  }>;
  currentIndex: number;
  branches: Array<{
    id: string;
    parentEntryId: string;
    entries: Array<{
      id: string;
      operations: Array<{ kind: string; range: { start: { line: number; column: number }; end: { line: number; column: number } }; text: string; origin: string }>;
      beforeCursors: Array<{ position: { line: number; column: number }; desiredColumn: number }>;
      afterCursors: Array<{ position: { line: number; column: number }; desiredColumn: number }>;
      timestamp: number;
      groupId?: string;
    }>;
  }>;
  activeBranch: string;
} {
  const serializeEntry = (entry: HistoryEntry) => ({
    id: entry.id,
    operations: entry.operations.map(op => ({
      kind: op.kind,
      range: {
        start: { line: op.range.start.line, column: op.range.start.column },
        end: { line: op.range.end.line, column: op.range.end.column },
      },
      text: op.text,
      origin: op.origin,
    })),
    beforeCursors: entry.beforeCursors.map(c => ({
      position: { line: c.position.line, column: c.position.column },
      desiredColumn: c.desiredColumn,
    })),
    afterCursors: entry.afterCursors.map(c => ({
      position: { line: c.position.line, column: c.position.column },
      desiredColumn: c.desiredColumn,
    })),
    timestamp: entry.timestamp,
    groupId: entry.groupId,
  });

  return {
    entries: state.entries.map(serializeEntry),
    currentIndex: state.currentIndex,
    branches: state.branches.map(b => ({
      id: b.id,
      parentEntryId: b.parentEntryId,
      entries: b.entries.map(serializeEntry),
    })),
    activeBranch: state.activeBranch,
  };
}

export function deserializeHistory(data: {
  entries: Array<{
    id: string;
    operations: Array<{ kind: string; range: { start: { line: number; column: number }; end: { line: number; column: number } }; text: string; origin: string }>;
    beforeCursors: Array<{ position: { line: number; column: number }; desiredColumn: number }>;
    afterCursors: Array<{ position: { line: number; column: number }; desiredColumn: number }>;
    timestamp: number;
    groupId?: string;
  }>;
  currentIndex: number;
  branches: Array<{
    id: string;
    parentEntryId: string;
    entries: Array<{
      id: string;
      operations: Array<{ kind: string; range: { start: { line: number; column: number }; end: { line: number; column: number } }; text: string; origin: string }>;
      beforeCursors: Array<{ position: { line: number; column: number }; desiredColumn: number }>;
      afterCursors: Array<{ position: { line: number; column: number }; desiredColumn: number }>;
      timestamp: number;
      groupId?: string;
    }>;
  }>;
  activeBranch: string;
}): HistoryState {
  const deserializeEntry = (raw: {
    id: string;
    operations: Array<{ kind: string; range: { start: { line: number; column: number }; end: { line: number; column: number } }; text: string; origin: string }>;
    beforeCursors: Array<{ position: { line: number; column: number }; desiredColumn: number }>;
    afterCursors: Array<{ position: { line: number; column: number }; desiredColumn: number }>;
    timestamp: number;
    groupId?: string;
  }): HistoryEntry => ({
    id: raw.id,
    operations: raw.operations.map(op => ({
      kind: op.kind as EditOperation['kind'],
      range: {
        start: { line: op.range.start.line, column: op.range.start.column },
        end: { line: op.range.end.line, column: op.range.end.column },
      },
      text: op.text,
      origin: op.origin as EditOperation['origin'],
    })),
    beforeCursors: raw.beforeCursors.map(c => ({
      position: { line: c.position.line, column: c.position.column },
      desiredColumn: c.desiredColumn,
    })),
    afterCursors: raw.afterCursors.map(c => ({
      position: { line: c.position.line, column: c.position.column },
      desiredColumn: c.desiredColumn,
    })),
    timestamp: raw.timestamp,
    groupId: raw.groupId,
  });

  return {
    entries: data.entries.map(deserializeEntry),
    currentIndex: data.currentIndex,
    branches: data.branches.map(b => ({
      id: b.id,
      parentEntryId: b.parentEntryId,
      entries: b.entries.map(deserializeEntry),
    })),
    activeBranch: data.activeBranch,
  };
}
